/**
 * Forgot Password Page
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo Component
const Logo = ({ className = "h-10 w-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_f315b95a-0efe-40b5-9cbe-765ee1480209/artifacts/fawkrmzt_logo%20512%20.512.png" 
    alt="Infirmly" 
    className={className}
  />
);

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      // In development, show the reset link
      if (res.data.reset_link) {
        setResetLink(res.data.reset_link);
      }
      toast.success('Wenn die E-Mail existiert, wurde ein Link gesendet');
    } catch (error) {
      toast.error('Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <Logo className="h-12 w-12" />
            <span className="font-bold text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>Infirmly Swiss</span>
          </Link>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Passwort vergessen?
          </h1>
          <p className="text-gray-500 mt-2">
            Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
          </p>
        </div>

        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-6">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="max@beispiel.ch"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 h-11"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-[#0044CC] hover:bg-[#003399] text-white font-medium rounded-full"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Link senden'}
                </Button>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">E-Mail gesendet!</h3>
                  <p className="text-gray-500 text-sm mt-2">
                    Wenn ein Konto mit dieser E-Mail existiert, erhalten Sie in Kürze einen Link zum Zurücksetzen Ihres Passworts.
                  </p>
                </div>
                {/* Development only - show reset link */}
                {resetLink && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-800 mb-2">
                      <strong>Entwicklungsmodus:</strong> Reset-Link:
                    </p>
                    <a 
                      href={resetLink} 
                      className="text-xs text-blue-600 hover:underline break-all"
                    >
                      {resetLink}
                    </a>
                  </div>
                )}
                <Button 
                  variant="outline"
                  className="mt-4"
                  onClick={() => { setSubmitted(false); setEmail(''); setResetLink(''); }}
                >
                  Andere E-Mail versuchen
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-gray-500">
          <Link to="/login" className="text-[#0044CC] hover:underline font-medium">
            Zurück zur Anmeldung
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
